import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { Timer } from './components/Timer';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SignIn } from './components/auth/SignIn';
import { SignUp } from './components/auth/SignUp';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { TimerProvider } from './contexts/TimerContext';
import { GamificationProvider } from './contexts/GamificationContext';
import { QuotesProvider } from './contexts/QuotesContext';
import { SocialProvider } from './contexts/SocialContext';
import { RankingsProvider } from './contexts/RankingsContext';
import { AuthProvider } from './contexts/AuthContext';
import { GoalProvider } from './contexts/GoalContext';
import { Toaster } from 'react-hot-toast';
import { TooltipProvider } from "@/components/ui/tooltip"
import { SharedGoalsProvider } from './contexts/SharedGoalsContext';
import { TaskProvider } from './contexts/TaskContext';
import { NotFound } from './components/NotFound';
import { ClerkProvider, clerkPublishableKey } from './config/clerk';
import { EnvCheck } from './components/EnvCheck';
import { AdminRoute } from './components/admin/AdminRoute';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { useSupabaseKeepalive } from './hooks/useSupabaseKeepalive';

const SupabaseKeepalive = () => { useSupabaseKeepalive(); return null; };

// Lazy-loaded routes for better performance
const Statistics = React.lazy(() => import('./components/Statistics').then(m => ({ default: m.Statistics })));
const DailyQuotes = React.lazy(() => import('./components/DailyQuotes').then(m => ({ default: m.DailyQuotes })));
const AboutUs = React.lazy(() => import('./components/AboutUs').then(m => ({ default: m.AboutUs })));
const FAQ = React.lazy(() => import('./components/FAQ').then(m => ({ default: m.FAQ })));
const Feedback = React.lazy(() => import('./components/Feedback').then(m => ({ default: m.Feedback })));
const Goals = React.lazy(() => import('./components/goals/Goals').then(m => ({ default: m.Goals })));
const Settings = React.lazy(() => import('./components/settings/Settings').then(m => ({ default: m.Settings })));
const RewardsPage = React.lazy(() => import('./pages/RewardsPage').then(m => ({ default: m.RewardsPage })));
const GoalFormPage = React.lazy(() => import('./pages/GoalFormPage').then(m => ({ default: m.GoalFormPage })));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const PricingPage = React.lazy(() => import('./components/pricing/PricingPage').then(m => ({ default: m.PricingPage })));
const SubscriptionSuccess = React.lazy(() => import('./components/pricing/SubscriptionSuccess').then(m => ({ default: m.SubscriptionSuccess })));
const SubscriptionCancel = React.lazy(() => import('./components/pricing/SubscriptionCancel').then(m => ({ default: m.SubscriptionCancel })));
const UserProfile = React.lazy(() => import('./components/profile/UserProfile').then(m => ({ default: m.UserProfile })));
const LearnPage = React.lazy(() => import('./pages/LearnPage').then(m => ({ default: m.LearnPage })));
const LearnVideoPage = React.lazy(() => import('./pages/LearnVideoPage').then(m => ({ default: m.LearnVideoPage })));
const LearnReviewPage = React.lazy(() => import('./pages/LearnReviewPage').then(m => ({ default: m.LearnReviewPage })));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

export const App = () => {
  // If Clerk key is missing or invalid, show a fallback
  if (!clerkPublishableKey || !clerkPublishableKey.startsWith('pk_')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
          <h2 className="text-xl font-semibold mb-4">Configuration Required</h2>
          <p className="text-muted-foreground mb-4">
            Please add your Clerk publishable key to the environment variables.
          </p>
          <p className="text-sm text-muted-foreground">
            Add <code className="bg-muted px-1 rounded">VITE_CLERK_PUBLISHABLE_KEY</code> to your Netlify environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <EnvCheck />
      <SupabaseKeepalive />
      <ClerkProvider 
        publishableKey={clerkPublishableKey}
        signInUrl="/signin"
        signUpUrl="/signup"
      >
        <TooltipProvider>
        <AuthProvider>
          <SocialProvider>
            <ThemeProvider>
              <SettingsProvider>
                <ProjectProvider>
                <SubscriptionProvider>
                  <TimerProvider>
                    <GamificationProvider>
                      <QuotesProvider>
                        <RankingsProvider>
                          <GoalProvider>
                            <SharedGoalsProvider>
                            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                            <Suspense fallback={<PageLoader />}>
                            <Routes>
                              {/* Public routes */}
                              <Route path="/signin" element={<SignIn />} />
                              <Route path="/signup" element={<SignUp />} />
                              <Route path="/about" element={<AboutUs />} />
                              <Route path="/faq" element={<FAQ />} />

                                {/* Protected routes */}
                                <Route path="/" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <Timer />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/timer" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <Timer />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/statistics" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <Statistics />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/quotes" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <DailyQuotes />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/feedback" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <Feedback />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                {/* Community disabled until moderation is added
                                <Route path="/community" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <CommunityHub />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                */}
                                <Route path="/goals" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <TaskProvider>
                                        <Goals />
                                      </TaskProvider>
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/goals/new" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <TaskProvider>
                                        <GoalFormPage />
                                      </TaskProvider>
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/goals/edit/:goalId" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <TaskProvider>
                                        <GoalFormPage />
                                      </TaskProvider>
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/profile/:userId" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <UserProfile />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                {/* Saved items disabled with community
                                <Route path="/saved-items" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <SavedItems />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                */}
                                <Route path="/settings" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <Settings />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/learn" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <LearnPage />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/learn/review" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <LearnReviewPage />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/learn/:videoId" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <LearnVideoPage />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/rewards" element={<RewardsPage />} />
                                <Route path="/pricing" element={
                                  <Layout>
                                    <PricingPage />
                                  </Layout>
                                } />
                                <Route path="/subscription/success" element={
                                  <ProtectedRoute>
                                    <Layout>
                                      <SubscriptionSuccess />
                                    </Layout>
                                  </ProtectedRoute>
                                } />
                                <Route path="/subscription/cancel" element={
                                  <Layout>
                                    <SubscriptionCancel />
                                  </Layout>
                                } />
                                <Route path="/admin" element={
                                  <AdminRoute>
                                    <Layout>
                                      <AdminDashboard />
                                    </Layout>
                                  </AdminRoute>
                                } />
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                            </Suspense>
                              <Toaster position="top-right" />
                            </Router>
                            </SharedGoalsProvider>
                          </GoalProvider>
                        </RankingsProvider>
                      </QuotesProvider>
                    </GamificationProvider>
                  </TimerProvider>
                </SubscriptionProvider>
                </ProjectProvider>
              </SettingsProvider>
            </ThemeProvider>
          </SocialProvider>
        </AuthProvider>
      </TooltipProvider>
    </ClerkProvider>
    </>
  );
};